import { useEffect, useRef, useState } from 'react'

import QueryString from 'qs'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import Button from 'core/components/lib/Button'
import { error, onSurfaceSecondary, primary } from 'core/styles'

import { FallbackProps } from './defaultErrorBoundaries'
import ErrorRecoveryActions from './ErrorRecoveryActions'

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;

  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }
`

const Title = styled.div`
  margin: 12px;
  padding: 6px 12px;
  color: ${onSurfaceSecondary};
  font-size: 20px;
  font-weight: bold;
`

const MessageText = styled.span`
  display: inline-block;
`

const SupportLink = styled.a`
  color: ${primary};

  &:hover {
    text-decoration: underline;
  }
`
const Bolder = styled.span`
  font-weight: 500;
`

export type ReportErrorFallbackProps = FallbackProps & {
  errorAt: string
  debugId: string
  peachRequestId?: string
  errorId?: string
}

const DefaultReportErrorFallback = ({
  className,
  errorAt,
  debugId,
  peachRequestId,
  errorId,
  reset,
}: ReportErrorFallbackProps) => {
  const { origin = '', pathname = '' } = window.location || {}
  const url = `${origin}${pathname}`

  const [copySuccess, setCopySuccess] = useState<null | boolean>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        `Error on page ${url} with Error ID ${debugId} at ${errorAt}${errorId ? ` (API errorId: ${errorId})` : ''}`,
      )
      setCopySuccess(true)
    } catch {
      setCopySuccess(false)
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setCopySuccess(null)
    }, 2000)
  }

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  return (
    <Message className={className}>
      <Title>An unexpected error has occurred</Title>
      <MessageText>
        <p>
          Please{' '}
          <SupportLink
            href={`https://peachfinance.zendesk.com/hc/en-us/requests/new?${QueryString.stringify({
              tf_subject: `Error on page ${url}`,
              tf_description: `<p></p><p>---<br/>Datetime: ${errorAt || 'n/a'}<br/>URL: ${url || 'n/a'}<br/>Error ID: ${
                debugId || 'n/a'
              }<br/>Request ID: ${peachRequestId || 'n/a'}${errorId ? `<br/>API errorId: ${errorId}` : ''}`,
            })}`}
            target='_blank'
          >
            contact Peach support
          </SupportLink>{' '}
          and describe what you were doing when this error occurred. Including a screenshot with your report can aid in
          identifying and resolving the issue.
        </p>
        <table>
          <tr>
            <td>
              <Bolder>Page:</Bolder>
            </td>
            <td>{url}</td>
          </tr>
          <tr>
            <td>
              <Bolder>Datetime:</Bolder>
            </td>
            <td>{errorAt}</td>
          </tr>
          <tr>
            <td>
              <Bolder>Error ID:</Bolder>
            </td>
            <td>{debugId}</td>
          </tr>
          {errorId && (
            <tr>
              <td>
                <Bolder>API errorId:</Bolder>
              </td>
              <td>{errorId}</td>
            </tr>
          )}
        </table>
      </MessageText>
      <Button color={primary} onClick={copyToClipboard}>
        {copySuccess === null ?
          <>
            Copy &nbsp;
            <Icon color={primary} name='content_copy' />
          </>
        : copySuccess ?
          <>
            Copied &nbsp;
            <Icon color={primary} name='check' />
          </>
        : <>
            Error &nbsp;
            <Icon color={error} name='warning' />
          </>
        }
      </Button>
      <ErrorRecoveryActions onRetry={reset} />
    </Message>
  )
}

export default DefaultReportErrorFallback
